import { escapeHtml as escape } from '../util/escapeHtml.js';
/**
 * Rex Mortis — Patch Notes / What's New
 *
 * Lists patch notes published by moderators / admins. Each entry has a
 * version, title, body (tiny markdown-ish: lines + bullet lists), and a
 * published date.
 */
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';

type Note = Awaited<ReturnType<typeof api.listPatchNotes>>['notes'][number];

interface Flash {
  kind: 'success' | 'danger';
  message: string;
}

/**
 * Tiny markdown-ish renderer:
 *  - Lines beginning with "- " become bullet list items
 *  - **bold** → <strong>
 *  - Blank lines separate paragraphs
 * Everything else is escaped.
 */
function renderBody(body: string): string {
  const lines = body.split(/\r?\n/);
  let html = '';
  let inList = false;
  let para: string[] = [];

  const flushPara = (): void => {
    if (para.length > 0) {
      html += `<p>${formatInline(para.join(' '))}</p>`;
      para = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith('- ')) {
      flushPara();
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      html += `<li>${formatInline(line.slice(2))}</li>`;
    } else if (line === '') {
      flushPara();
      if (inList) {
        html += '</ul>';
        inList = false;
      }
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      para.push(line);
    }
  }
  flushPara();
  if (inList) html += '</ul>';
  return html;
}

function formatInline(text: string): string {
  return escape(text).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

export async function renderPatchNotes(root: HTMLElement): Promise<void> {
  let notes: Note[] = [];
  let flash: Flash | null = null;

  try {
    const r = await api.listPatchNotes();
    notes = r.notes;
  } catch (err) {
    flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not load patch notes.' };
  }

  mountShell(root, (main) => {
    main.innerHTML = `
      <img class="page-banner" src="/img/banners/patch-notes.webp" alt="" />
      <h1>Patch Notes</h1>
      <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
        What changed in the realm.
      </p>

      ${flash ? `<div class="banner banner-${flash.kind}">${flash.message}</div>` : ''}

      ${
        notes.length === 0
          ? '<p class="caption">No patch notes yet. Check back after the next release.</p>'
          : notes
              .map(
                (n) => `
                  <article class="panel" style="margin-top: var(--s-3);">
                    <div style="display:flex; flex-wrap:wrap; gap: var(--s-2); align-items: baseline; justify-content:space-between;">
                      <h2 style="margin: 0;">${escape(n.title)}</h2>
                      <span class="caption">v${escape(n.version.replace(/^v/i, ''))} · ${new Date(n.publishedAt).toLocaleDateString()}</span>
                    </div>
                    <div class="patch-body" style="margin-top: var(--s-2); line-height: 1.55;">
                      ${renderBody(n.body)}
                    </div>
                  </article>
                `,
              )
              .join('')
      }
    `;
  });
}
